import { useCallback, useEffect, useRef, useState } from "react"
import { useIntersection } from "@mantine/hooks"

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches

// Scroll-reveal wrapper, HYDRATION-SAFE (act:7079ffd1). The funnel landing is
// server-rendered, and React hydration requires the first client render to
// match the server markup byte-for-byte. The old shape initialized
// `visible` from `typeof IntersectionObserver` — false in the browser, true
// under node — so every hydration failed deterministically and React threw
// the server DOM away (CP1 automation). The invariant now: the INITIAL render
// is always visible, on the server, on the client, everywhere. Hiding for the
// scroll animation happens only AFTER mount (a normal update, invisible to
// hydration), and only for elements still entirely below the viewport — so
// nothing a person can see ever flashes, and above-the-fold content ships
// painted (CLS 0 preserved).
//
// WHAT IS MECHANICALLY GUARDED NOW, AND WHAT IS STILL ONLY THIS COMMENT
// (act:35de96ff). This is the canonical statement; CountUp.jsx,
// ScrollProgressBar.jsx, Landing.jsx and QualForm.jsx point here rather than
// restating it. Until that action, the discipline recorded across those five
// files was the ONLY thing between an ordinary edit and a broken funnel first
// paint. HALF of it now has a gate: spec/frontend/funnel_ssr_render_spec.rb
// renders this page through the real SSR bundle in a Node process on every push,
// so a browser global read at module scope or in a render body — anything that
// THROWS under node — turns CI red instead of silently degrading production to
// client-side rendering. `yarn lint`'s two no-dom-globals rules catch the same
// class lexically, before a build even runs (platform/eslint.config.js).
//
// The OTHER half — HYDRATION DIVERGENCE, which is the defect the invariant above
// actually exists to prevent — is guarded by NEITHER. A first client render that
// merely DISAGREES with the server throws nothing: the server render succeeds,
// that spec passes, the lint sees nothing, and React discards the server DOM at
// runtime with no error anywhere. That is precisely what the old
// `typeof IntersectionObserver` shape did. Catching it needs a server-vs-client
// first-render diff, which act:35de96ff deliberately deferred. So for the thing
// this comment describes, this comment is still the guard.
export default function Reveal({ children, className = "", delay = 0, as: Tag = "div" }) {
  const [visible, setVisible] = useState(true)
  const nodeRef = useRef(null)
  const { ref: observe, entry } = useIntersection({
    threshold: 0.12,
    rootMargin: "0px 0px -40px 0px",
  })
  // MEMOIZED, load-bearing (CP3 boundary-man, blocking): an inline combiner
  // is a fresh function every render, so React detaches/reattaches it each
  // commit — and Mantine's intersection ref responds to every reattach with
  // a state change and a brand-new IntersectionObserver, whose initial
  // notification triggers the next commit. Measured on the landing: ~600 new
  // observers/second, sustained, with a clean console. Mantine's `observe`
  // is stable (keyed on the primitive options), so this never cycles.
  const setRefs = useCallback(
    (node) => {
      nodeRef.current = node
      observe(node)
    },
    [observe],
  )

  // Post-mount arming. Client-only by construction (effects never run during
  // SSR), and post-hydration by construction (effects run after the hydration
  // commit) — that ordering is what makes hiding here mismatch-free.
  useEffect(() => {
    const canAnimate = typeof IntersectionObserver !== "undefined" && !prefersReducedMotion()
    if (!canAnimate) return
    const rect = nodeRef.current?.getBoundingClientRect()
    // Entirely below the viewport: hiding it is invisible to the visitor, and
    // the observer reveals it on scroll. A partially-visible element stays
    // shown — hiding it would be a visible flash.
    if (rect && rect.top > window.innerHeight) setVisible(false)
    // Arm once, on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!visible && entry?.isIntersecting) setVisible(true)
  }, [entry, visible])

  // Belt-and-braces for a genuinely BROKEN observer only (CP3 boundary-man):
  // gated on `entry` because a healthy IntersectionObserver always delivers
  // an initial entry right after attach — so this timer arms only in the
  // window before that proof-of-life arrives and is cleared by it. Without
  // the gate it fired for every below-fold element at 1.5s, silently
  // retiring the scroll-reveal design for any section reached later.
  useEffect(() => {
    if (visible || entry) return
    const fallback = setTimeout(() => setVisible(true), 1500)
    return () => clearTimeout(fallback)
  }, [visible, entry])

  return (
    <Tag
      ref={setRefs}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      className={`reveal ${visible ? "reveal-visible" : ""} ${className}`.trim()}
    >
      {children}
    </Tag>
  )
}
